/**
 * MinistrareIA - WhatsApp Business Inbox Page
 * Padronizado com WhatsApp Pessoal
 * 
 * Diferenças mantidas:
 * - OAuth-based connection (no QR Code)
 * - Filters SSE events by source: 'wa_business'
 * - ConnectionModal específico para OAuth
 * - Token expired warning
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wifi, WifiOff, Menu, X, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSSE, useSSEEvent, SSE_EVENTS, SSE_STATES } from '../../contexts/SSEContext';
import waBusinessService from '../../services/wa-business/waBusinessService';
import authService from '../../services/global/authService';

// Components
import NavSidebar from '../../components/NavSidebar';
import ChatList from './ChatList';
import ChatWindow from './ChatWindow';
import ConnectionModal from '../../components/wa-business/ConnectionModal';

// Pages (shared with wa-personal)
import LabelsPage from '../global/LabelsPage';
import AgentsPage from '../global/AgentsPage';
import TrainingPage from '../global/TrainingPage';
import FollowUpsPage from '../global/FollowUpsPage';
import LeadsPage from '../global/LeadsPage';
import ProductsPage from '../global/ProductsPage';
import VideoLinksPage from '../global/VideoLinksPage';
import TemplatesPage from './TemplatesPage';
import TicketsPage from './TicketsPage';
import RemindersPage from '../dr-salus/RemindersPage';
import PlansPage from '../dr-salus/PlansPage';
import SubscribersPage from '../dr-salus/SubscribersPage';
import ControlPanelPage from '../dr-salus/ControlPanelPage';
import SafetyMonitorPage from '../dr-salus/SafetyMonitorPage';

const WABusinessInbox = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme, colors } = useTheme();
  
  // SSE context
  const { isConnected: sseConnected, sseState, reconnectInfo, reconnect: reconnectSSE } = useSSE();

  // Navigation state (igual ao pessoal)
  const [currentPage, setCurrentPage] = useState('inbox');

  // Chat state
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState({ 
    connected: false, 
    status: 'checking',
    phone_number: null,
    verified_name: null,
    token_expired: false 
  });
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [loadingChats, setLoadingChats] = useState(true);
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDisconnectPopup, setShowDisconnectPopup] = useState(true);

  // Autopilot state
  const [autopilotContacts, setAutopilotContacts] = useState([]);
  const [blockedContacts, setBlockedContacts] = useState([]);

  // Refs
  const selectedChatRef = useRef(selectedChat);
  const chatsRef = useRef(chats);
  const syncPollingRef = useRef(null);

  // Keep refs in sync
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup sync polling on unmount
  useEffect(() => {
    return () => {
      if (syncPollingRef.current) {
        clearInterval(syncPollingRef.current);
        syncPollingRef.current = null;
      }
    };
  }, []);

  // Load autopilot config on mount
  useEffect(() => {
    const loadAutopilotConfig = async () => {
      try {
        const response = await authService.getAutopilotConfig();
        if (response?.data?.autopilot_contacts) {
          setAutopilotContacts(response.data.autopilot_contacts);
        }
        if (response?.data?.blocked_contacts) {
          setBlockedContacts(response.data.blocked_contacts);
        }
      } catch (err) {
        console.error('[WA Business Inbox] Error loading autopilot config:', err);
      }
    };
    loadAutopilotConfig();
  }, []);

  // Handler: toggle autopilot for a specific contact
  const handleToggleAutopilot = useCallback(async (phone, enabled) => {
    try {
      const response = await authService.toggleAutopilotContact(phone, enabled);
      if (response?.data?.autopilot_contacts) {
        setAutopilotContacts(response.data.autopilot_contacts);
      }
      if (response?.data?.blocked_contacts) {
        setBlockedContacts(response.data.blocked_contacts);
      }
    } catch (err) {
      console.error('[WA Business Inbox] Error toggling autopilot:', err);
    }
  }, []);

  // Handler: toggle blocked status for a specific contact
  const handleToggleBlockedContact = useCallback(async (phone, blocked) => {
    try {
      const response = await authService.toggleBlockedContact(phone, blocked);
      if (response?.data?.blocked_contacts) {
        setBlockedContacts(response.data.blocked_contacts);
      }
      if (response?.data?.autopilot_contacts) {
        setAutopilotContacts(response.data.autopilot_contacts);
      }
    } catch (err) {
      console.error('[WA Business Inbox] Error toggling blocked contact:', err);
    }
  }, []);

  // ============================================
  // SSE EVENT HANDLERS (WA Business only)
  // ============================================

  // Handle NEW_MESSAGE from SSE
  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    // Only process WA Business messages
    if (data?.source !== 'wa_business') return;
    
    console.log('[WA Business Inbox] SSE NEW_MESSAGE:', data?.chat_id);
    
    if (data && data.chat_id) {
      updateChatWithNewMessage(data);
    }
  }, []);

  // Handle MESSAGE_STATUS from SSE
  useSSEEvent(SSE_EVENTS.MESSAGE_STATUS, (data) => {
    // Only process WA Business status updates
    if (data?.source !== 'wa_business') return;
    
    console.log('[WA Business Inbox] SSE MESSAGE_STATUS:', data?.wamid);
    // Status updates are handled in ChatWindow
  }, []);

  // Handle CHAT_UPDATE from SSE
  useSSEEvent(SSE_EVENTS.CHAT_UPDATE, (data) => {
    // Only process WA Business chat updates
    if (data?.source !== 'wa_business') return;

    console.log('[WA Business Inbox] SSE CHAT_UPDATE');
    loadChats(true);
  }, []);

  // Handle SYNC_CHAT_ADDED from SSE (progressive sync after OAuth)
  useSSEEvent(SSE_EVENTS.SYNC_CHAT_ADDED, (data) => {
    if (data?.source !== 'wa_business') return;
    console.log('[WA Business Inbox] SSE SYNC_CHAT_ADDED:', data?.total_chats);
    loadChats(true);
  }, []);

  // Handle SYNC_PROGRESS from SSE
  useSSEEvent(SSE_EVENTS.SYNC_PROGRESS, (data) => {
    if (data?.source !== 'wa_business') return;
    console.log('[WA Business Inbox] SSE SYNC_PROGRESS:', data?.meta_progress, '%');
  }, []);

  // Handle SYNC_COMPLETED from SSE (final reload after history sync)
  useSSEEvent(SSE_EVENTS.SYNC_COMPLETED, (data) => {
    if (data?.source !== 'wa_business') return;
    console.log('[WA Business Inbox] SSE SYNC_COMPLETED:', data?.chats_synced, 'chats');

    // Stop polling fallback if active
    if (syncPollingRef.current) {
      clearInterval(syncPollingRef.current);
      syncPollingRef.current = null;
    }

    loadChats(true);
  }, []);

  // ============================================
  // DATA LOADING
  // ============================================

  const checkConnection = useCallback(async () => {
    try {
      const response = await waBusinessService.getStatus();
      const data = response || { connected: false, status: 'disconnected' };
      
      setConnectionStatus(data);
      
      if (data.token_expired) {
        console.warn('[WA Business Inbox] Token expired');
      }
      
      return data.connected;
    } catch (err) {
      console.error('[WA Business Inbox] Error checking connection:', err);
      setConnectionStatus({ connected: false, status: 'error' });
      return false;
    }
  }, []);

  const loadChats = useCallback(async (silent = false) => {
    if (!silent) setLoadingChats(true);
    try {
      const response = await waBusinessService.getChats(50, 0);
      const newChats = response.data || [];
      
      // Sort by last_message_at (newest first)
      const sortedChats = [...newChats].sort((a, b) => {
        const dateA = new Date(a.last_message_at || a.updated_at || 0);
        const dateB = new Date(b.last_message_at || b.updated_at || 0);
        return dateB - dateA;
      });
      
      setChats(sortedChats);
    } catch (err) {
      console.error('[WA Business Inbox] Error loading chats:', err);
      if (!silent) setChats([]);
    } finally {
      if (!silent) setLoadingChats(false);
    }
  }, []);

  // ============================================
  // CHAT UPDATE LOGIC
  // ============================================

  const updateChatWithNewMessage = useCallback((data) => {
    const { chat_id, message, contact } = data;
    
    if (!chat_id) return;
    
    setChats(prevChats => {
      const existingIndex = prevChats.findIndex(c => c.id === chat_id);
      
      if (existingIndex >= 0) {
        const newChats = [...prevChats];
        const [existingChat] = newChats.splice(existingIndex, 1);
        
        const updatedChat = {
          ...existingChat,
          contact_name: existingChat.contact_name || contact?.name,
          last_message_text: message?.text || `📎 ${message?.type || 'mídia'}`,
          last_message_at: message?.timestamp || new Date().toISOString(),
          last_message_from_me: message?.from_me || false,
          unread_count: message?.from_me 
            ? existingChat.unread_count 
            : (existingChat.unread_count || 0) + 1,
        };
        
        return [updatedChat, ...newChats];
      } else {
        loadChats(true);
        return prevChats;
      }
    });
  }, [loadChats]);

  // ============================================
  // INITIAL LOAD
  // ============================================

  useEffect(() => {
    const init = async () => {
      const isConnected = await checkConnection();
      if (isConnected) {
        await loadChats(false);
      } else {
        setShowConnectionModal(true);
        setLoadingChats(false);
      }
    };
    init();
  }, [checkConnection, loadChats]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  const handleConnectionSuccess = async (accountData) => {
    console.log('[WA Business Inbox] Connection success:', accountData);
    setShowConnectionModal(false);

    setConnectionStatus({
      connected: true,
      status: 'connected',
      phone_number: accountData?.phone_number,
      verified_name: accountData?.verified_name,
      token_expired: false,
    });

    // Load chats immediately (may be empty while sync runs)
    await loadChats(false);

    // Polling fallback: reload chats periodically until sync completes
    // Stops on SYNC_COMPLETED event or after max attempts
    if (syncPollingRef.current) {
      clearInterval(syncPollingRef.current);
    }
    let attempts = 0;
    const maxAttempts = 10;
    syncPollingRef.current = setInterval(async () => {
      attempts++;
      console.log(`[WA Business Inbox] Sync polling ${attempts}/${maxAttempts}`);
      await loadChats(true);
      if (attempts >= maxAttempts && syncPollingRef.current) {
        clearInterval(syncPollingRef.current);
        syncPollingRef.current = null;
      }
    }, 5000);
  };

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    
    setChats(prevChats => 
      prevChats.map(c => 
        c.id === chat.id ? { ...c, unread_count: 0 } : c
      )
    );
    
    if (isMobile) setShowSidebar(false);
  };

  const handleBackToChats = () => {
    setSelectedChat(null);
    setShowSidebar(true);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await checkConnection();
    if (connectionStatus.connected) {
      await loadChats(false);
    }
    setIsRefreshing(false);
  };

  const handleMessageSent = (messageData) => {
    if (selectedChat && messageData) {
      updateChatWithNewMessage({
        chat_id: selectedChat.id,
        message: {
          text: messageData.text,
          timestamp: new Date().toISOString(),
          from_me: true,
        },
      });
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Deseja desconectar sua conta WhatsApp Business?')) return;
    
    try {
      await waBusinessService.disconnect();
      handleDisconnected();
    } catch (err) {
      console.error('[WA Business Inbox] Error disconnecting:', err);
      alert('Erro ao desconectar. Tente novamente.');
    }
  };

  // Callback when disconnected (from modal or direct)
  const handleDisconnected = () => {
    console.log('[WA Business Inbox] Account disconnected');
    setConnectionStatus({ connected: false, status: 'disconnected' });
    setChats([]);
    setSelectedChat(null);
    setShowDisconnectPopup(true);
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleOpenConnection = () => setShowConnectionModal(true);

  // ============================================
  // STYLES (Padronizado com WhatsApp Pessoal)
  // ============================================

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      width: '100vw',
      overflow: 'hidden',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    mainArea: { 
      flex: 1, 
      display: 'flex', 
      overflow: 'hidden' 
    },
    inboxLayout: { 
      flex: 1, 
      display: 'flex', 
      overflow: 'hidden' 
    },
    sidebar: {
      width: isMobile ? '100%' : '395px',
      minWidth: isMobile ? '100%' : '395px',
      maxWidth: isMobile ? '100%' : '395px',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgPrimary,
      borderRight: `1px solid ${colors.borderColor}`,
      position: isMobile ? 'fixed' : 'relative',
      left: 0, 
      top: 0, 
      zIndex: 50,
      transform: isMobile && !showSidebar ? 'translateX(-100%)' : 'translateX(0)',
      transition: 'transform 0.3s ease',
    },
    chatArea: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgChat,
      overflow: 'hidden',
    },
    mobileHeader: {
      display: isMobile ? 'flex' : 'none',
      alignItems: 'center',
      padding: '12px 16px',
      backgroundColor: colors.headerBg,
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    mobileMenuBtn: {
      width: 40, 
      height: 40,
      border: 'none', 
      background: 'transparent',
      color: colors.textPrimary, 
      cursor: 'pointer',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
    },
    mobileTitle: {
      flex: 1, 
      fontWeight: 500, 
      fontSize: 18, 
      textAlign: 'center', 
      color: colors.textPrimary,
    },
    disconnectionPopup: {
      position: 'fixed', 
      bottom: 60, 
      left: '50%', 
      transform: 'translateX(-50%)',
      backgroundColor: colors.bgPrimary, 
      color: colors.textPrimary,
      padding: '12px 20px', 
      display: 'flex', 
      alignItems: 'center', 
      gap: 12,
      zIndex: 90, 
      borderRadius: 12, 
      border: `1px solid ${colors.danger}`, 
      boxShadow: colors.shadowLg,
    },
    disconnectionBtn: {
      padding: '6px 14px', 
      backgroundColor: colors.danger, 
      color: 'white',
      border: 'none', 
      borderRadius: 6, 
      cursor: 'pointer', 
      fontSize: 13, 
      fontWeight: 500,
    },
    tokenWarning: {
      position: 'fixed',
      bottom: 60,
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: `${colors.warning || '#f59e0b'}15`,
      border: `1px solid ${colors.warning || '#f59e0b'}40`,
      color: colors.textPrimary,
      padding: '12px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      zIndex: 90,
      borderRadius: 12,
      boxShadow: colors.shadowLg,
    },
    reconnectBtn: {
      padding: '6px 14px', 
      backgroundColor: colors.accentPrimary, 
      color: 'white',
      border: 'none', 
      borderRadius: 6, 
      cursor: 'pointer', 
      fontSize: 13, 
      fontWeight: 500,
    },
    banner: {
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0,
      color: 'white', 
      padding: '8px 16px',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      gap: 8,
      fontSize: 14, 
      zIndex: 100,
    },
  };

  // ============================================
  // RENDER
  // ============================================

  const renderPageContent = () => {
    switch (currentPage) {
      case 'leads': return <LeadsPage />;
      case 'followups': return <FollowUpsPage />;
      case 'templates': return <TemplatesPage />;
      case 'reminders': return <RemindersPage />;
      case 'plans': return <PlansPage />;
      case 'subscribers': return <SubscribersPage />;
      case 'control-panel': return <ControlPanelPage />;
      case 'safety-monitor': return <SafetyMonitorPage />;
      case 'tickets': return <TicketsPage />;
      case 'products': return <ProductsPage />;
      case 'video-links': return <VideoLinksPage />;
      case 'labels': return <LabelsPage />;
      case 'agents': return <AgentsPage />;
      case 'training': return <TrainingPage />;
      case 'inbox':
      default:
        return (
          <div style={styles.inboxLayout}>
            <aside style={styles.sidebar}>
              <ChatList
                chats={chats}
                selectedChat={selectedChat}
                onSelectChat={handleSelectChat}
                loading={loadingChats}
              />
            </aside>

            <main style={styles.chatArea}>
              {isMobile && (
                <div style={styles.mobileHeader}>
                  {selectedChat ? (
                    <>
                      <button style={styles.mobileMenuBtn} onClick={handleBackToChats}>
                        <X size={24} />
                      </button>
                      <span style={styles.mobileTitle}>
                        {selectedChat.contact_name || selectedChat.contact_phone}
                      </span>
                    </>
                  ) : (
                    <>
                      <button style={styles.mobileMenuBtn} onClick={() => setShowSidebar(true)}>
                        <Menu size={24} />
                      </button>
                      <span style={styles.mobileTitle}>MinistrareIA</span>
                    </>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    {connectionStatus.connected ? (
                      <Wifi size={16} color={colors.accentPrimary} />
                    ) : (
                      <WifiOff size={16} color={colors.danger} />
                    )}
                  </div>
                </div>
              )}

              <ChatWindow
                chat={selectedChat}
                onMessageSent={handleMessageSent}
                autopilotContacts={autopilotContacts}
                onToggleAutopilot={handleToggleAutopilot}
                blockedContacts={blockedContacts}
                onToggleBlockedContact={handleToggleBlockedContact}
              />
            </main>
          </div>
        );
    }
  };

  return (
    <div style={styles.container}>
      {/* SSE Failed Banner */}
      {sseState === SSE_STATES.FAILED && (
        <div 
          style={{ ...styles.banner, backgroundColor: '#ef4444', cursor: 'pointer' }} 
          onClick={reconnectSSE}
        >
          <WifiOff size={16} />
          <span>Conexão perdida. Clique para reconectar.</span>
        </div>
      )}

      {/* Reconnecting Banner */}
      {reconnectInfo && (
        <div style={{ ...styles.banner, backgroundColor: colors.warning || '#f59e0b' }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          <span>Reconectando... ({reconnectInfo.attempt}/{reconnectInfo.maxAttempts})</span>
        </div>
      )}

      {/* Navigation Sidebar (igual ao pessoal) */}
      <NavSidebar
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onRefresh={handleRefresh}
        onOpenConnection={handleOpenConnection}
        isRefreshing={isRefreshing}
      />

      {/* Main Area */}
      <div style={styles.mainArea}>
        {renderPageContent()}
      </div>

      {/* Connection Modal (OAuth - específico do Business) */}
      <ConnectionModal
        isOpen={showConnectionModal}
        onClose={() => setShowConnectionModal(false)}
        onConnected={handleConnectionSuccess}
        connectionStatus={connectionStatus}
        onDisconnected={handleDisconnected}
      />

      {/* Token Expired Warning (específico do Business) */}
      {connectionStatus.connected && connectionStatus.token_expired && (
        <div style={styles.tokenWarning}>
          <AlertCircle size={18} color={colors.warning || '#f59e0b'} />
          <span style={{ fontSize: 14 }}>
            Token expirado. Reconecte sua conta.
          </span>
          <button 
            style={styles.reconnectBtn} 
            onClick={() => setShowConnectionModal(true)}
          >
            Reconectar
          </button>
        </div>
      )}

      {/* Disconnection Popup */}
      {currentPage === 'inbox' && 
       !connectionStatus.connected && 
       connectionStatus.status !== 'checking' && 
       !showConnectionModal &&
       showDisconnectPopup && (
        <div style={styles.disconnectionPopup}>
          <WifiOff size={18} style={{ color: colors.danger }} />
          <span style={{ fontSize: 14, color: colors.textSecondary }}>WhatsApp desconectado</span>
          <button style={styles.disconnectionBtn} onClick={() => setShowConnectionModal(true)}>
            Reconectar
          </button>
          <button 
            style={{ padding: 4, background: 'transparent', border: 'none', color: colors.textTertiary, cursor: 'pointer' }}
            onClick={() => setShowDisconnectPopup(false)}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { overflow: hidden; }
      `}</style>
    </div>
  );
};

export default WABusinessInbox;